/**
 * The translations library, as the API, the admin page and the agent share it
 * (docs/translations/01-plan.md).
 *
 * The agent's `translate-ad` skill dubs and subtitles a finished ad into other
 * languages; this is where the results are kept. The unit is the hook variant
 * (`editorId/conceptId/hook/iteration`, the key the tracker sheet and Meta's
 * numbers already share), and a translation is one (variant, language) pair.
 * A new upload of the same pair is a new version, the newest is current, and
 * nothing is ever deleted: a bad version is archived.
 */
import type { JobErrorCode } from "./job-errors.js";

/** A language as the pipeline names it: a BCP-47 tag (`es-ES`, `de`, `pt-BR`, `zh`). */
export interface TranslationLanguage {
  code: string;
  /** The English name, for the agent and for sorting. */
  name: string;
  /** What a speaker calls it. */
  nativeName: string;
}

/**
 * The catalogue: every language `translate-ad/translate.config.json` knows, in
 * its order, then the other common ones. Any valid tag is accepted whether or
 * not it is listed here; the list is only what the page can name without
 * asking the browser.
 */
export const TRANSLATION_LANGUAGES: readonly TranslationLanguage[] = [
  { code: "es-ES", name: "Spanish (Spain)", nativeName: "Español (España)" },
  { code: "es-MX", name: "Spanish (Mexico)", nativeName: "Español (México)" },
  { code: "de", name: "German", nativeName: "Deutsch" },
  { code: "fr", name: "French", nativeName: "Français" },
  { code: "it", name: "Italian", nativeName: "Italiano" },
  { code: "pt-PT", name: "Portuguese (Portugal)", nativeName: "Português (Portugal)" },
  { code: "pt-BR", name: "Portuguese (Brazil)", nativeName: "Português (Brasil)" },
  { code: "nl", name: "Dutch", nativeName: "Nederlands" },
  { code: "pl", name: "Polish", nativeName: "Polski" },
  { code: "sv", name: "Swedish", nativeName: "Svenska" },
  { code: "da", name: "Danish", nativeName: "Dansk" },
  { code: "no", name: "Norwegian", nativeName: "Norsk" },
  { code: "fi", name: "Finnish", nativeName: "Suomi" },
  { code: "cs", name: "Czech", nativeName: "Čeština" },
  { code: "ro", name: "Romanian", nativeName: "Română" },
  { code: "hu", name: "Hungarian", nativeName: "Magyar" },
  { code: "tr", name: "Turkish", nativeName: "Türkçe" },
  { code: "vi", name: "Vietnamese", nativeName: "Tiếng Việt" },
  { code: "id", name: "Indonesian", nativeName: "Bahasa Indonesia" },
  { code: "ru", name: "Russian", nativeName: "Русский" },
  { code: "uk", name: "Ukrainian", nativeName: "Українська" },
  { code: "el", name: "Greek", nativeName: "Ελληνικά" },
  { code: "ar", name: "Arabic", nativeName: "العربية" },
  { code: "he", name: "Hebrew", nativeName: "עברית" },
  { code: "hi", name: "Hindi", nativeName: "हिन्दी" },
  { code: "th", name: "Thai", nativeName: "ไทย" },
  { code: "ja", name: "Japanese", nativeName: "日本語" },
  { code: "ko", name: "Korean", nativeName: "한국어" },
  { code: "zh", name: "Chinese (Mandarin)", nativeName: "中文" },
  { code: "en", name: "English", nativeName: "English" },
  { code: "en-US", name: "English (US)", nativeName: "English (US)" },
  { code: "en-GB", name: "English (UK)", nativeName: "English (UK)" },
  { code: "es", name: "Spanish", nativeName: "Español" },
  { code: "pt", name: "Portuguese", nativeName: "Português" },
  { code: "zh-TW", name: "Chinese (Taiwan)", nativeName: "中文 (台灣)" },
  { code: "bg", name: "Bulgarian", nativeName: "Български" },
  { code: "hr", name: "Croatian", nativeName: "Hrvatski" },
  { code: "sk", name: "Slovak", nativeName: "Slovenčina" },
  { code: "sl", name: "Slovenian", nativeName: "Slovenščina" },
  { code: "sr", name: "Serbian", nativeName: "Српски" },
  { code: "lt", name: "Lithuanian", nativeName: "Lietuvių" },
  { code: "lv", name: "Latvian", nativeName: "Latviešu" },
  { code: "et", name: "Estonian", nativeName: "Eesti" },
  { code: "ca", name: "Catalan", nativeName: "Català" },
  { code: "ms", name: "Malay", nativeName: "Bahasa Melayu" },
  { code: "fil", name: "Filipino", nativeName: "Filipino" },
  { code: "fa", name: "Persian", nativeName: "فارسی" },
  { code: "bn", name: "Bengali", nativeName: "বাংলা" },
];

/** How many languages lead the page. */
export const PINNED_LANGUAGE_LIMIT = 10;

/** The owner's ten, in his order: what a new database pins until an admin changes it. */
export const DEFAULT_PINNED_LANGUAGES: readonly string[] = ["da", "sv", "de", "nl", "no", "fr", "ar", "hu", "fi", "it"];

/**
 * The country whose flag stands for a language (ISO 3166-1 alpha-2), or null
 * when none fits. A tag's own region wins (`es-MX` is Mexico, `pt-BR` Brazil);
 * otherwise the market we sell the language in (`ar` is the UAE, our main
 * Arabic market; `en` is the UK), else where the language is at home (`ca`
 * Andorra, its one official language; `fil` the Philippines). The page draws
 * these as bundled SVGs, never emoji: Windows shows emoji flags as letters.
 */
const LANGUAGE_COUNTRY: Readonly<Record<string, string>> = {
  es: "ES", de: "DE", fr: "FR", it: "IT", pt: "PT", nl: "NL", pl: "PL", sv: "SE", da: "DK", no: "NO", nb: "NO", nn: "NO",
  fi: "FI", cs: "CZ", ro: "RO", hu: "HU", tr: "TR", vi: "VN", id: "ID", ru: "RU", uk: "UA", el: "GR", ar: "AE", he: "IL",
  hi: "IN", th: "TH", ja: "JP", ko: "KR", zh: "CN", en: "GB", bg: "BG", hr: "HR", sk: "SK", sl: "SI", sr: "RS", lt: "LT",
  lv: "LV", et: "EE", ca: "AD", ms: "MY", fil: "PH", fa: "IR", bn: "BD", is: "IS", ga: "IE", mt: "MT", sq: "AL", mk: "MK",
  bs: "BA", ka: "GE", hy: "AM", az: "AZ", kk: "KZ", ur: "PK", ta: "LK", sw: "KE", af: "ZA", tl: "PH",
};

export function languageCountry(code: string): string | null {
  const [language, ...rest] = code.split("-");
  const region = rest.find((part) => /^[A-Z]{2}$/.test(part));
  return region ?? LANGUAGE_COUNTRY[language ?? ""] ?? null;
}

/**
 * The shape accepted after canonicalisation: a language (2-3 letters), an
 * optional script (`Hans`), an optional region (`ES`, `419`). No variants or
 * extensions: a file is in a language, not in a private-use subtag.
 */
const TAG = /^[a-z]{2,3}(?:-[A-Z][a-z]{3})?(?:-(?:[A-Z]{2}|\d{3}))?$/;

/**
 * A language tag in its one spelling (`es_es`, `ES-es` and `es-ES` are all
 * `es-ES`), or null when it is not a usable BCP-47 tag. The canonical form is
 * what is stored, so the same language never appears under two keys.
 */
export function normalizeLanguage(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const text = raw.trim().replace(/_/g, "-");
  if (!text || text.length > 35) return null;
  let canonical: string;
  try {
    canonical = Intl.getCanonicalLocales(text)[0] ?? "";
  } catch {
    return null;
  }
  return TAG.test(canonical) ? canonical : null;
}

/** The catalogue's entry for a tag, or null for a valid tag it does not list. */
export function translationLanguage(code: string): TranslationLanguage | null {
  return TRANSLATION_LANGUAGES.find((l) => l.code === code) ?? null;
}

/**
 * A variant key's parts, or null for a string that is not one. The editor is
 * required; any other part may be `none`.
 */
export function parseVariantKey(key: string): { editorId: number; conceptId: number | null; hook: number | null; iteration: number | null } | null {
  const m = /^(\d{1,9})\/(\d{1,9}|none)\/(\d{1,9}|none)\/(\d{1,9}|none)$/.exec(key.trim());
  if (!m) return null;
  const part = (v: string) => (v === "none" ? null : Number(v));
  return { editorId: Number(m[1]), conceptId: part(m[2]!), hook: part(m[3]!), iteration: part(m[4]!) };
}

/* ------------------------------------------------------------------ shapes */

/** How the uploader named the original, as the resolver matched it. */
export type OriginalMatch = "variant_key" | "drive_file_id" | "file_name";

/** The hook variant a translation belongs to. */
export interface TranslationVariant {
  variantKey: string;
  /** The URL form (`1-3014-1-x`), for `/creatives/:slug`. */
  slug: string;
  editorId: number;
  editorName: string | null;
  /** The editor's naming code (`CRIS`), the NAME their file names start with; null until an admin sets it. */
  editorCode: string | null;
  /** The editor's dot colour, as the leaderboard draws it. */
  editorColor: string | null;
  conceptId: number | null;
  hook: number | null;
  iteration: number | null;
  /** The original's file name as the tracker (or Meta) has it. */
  name: string | null;
  driveFileId: string | null;
  /** Lifetime Meta spend of the original, in euros; null when Meta has never run it. */
  spend: number | null;
}

/** One stored version of one translation. */
export interface TranslationFile {
  id: number;
  variantKey: string;
  language: string;
  /** 1, 2, 3... per (variant, language); null while the upload is pending. */
  version: number | null;
  status: "pending" | "ready";
  /** The newest version that is not archived. */
  current: boolean;
  archived: boolean;
  sha256: string;
  bytes: number;
  contentType: string;
  durationMs: number | null;
  width: number | null;
  height: number | null;
  /** The uploaded file's own name, as the uploader sent it. */
  fileName: string | null;
  /** The subtitles beside it, when they were sent. */
  srt: { sha256: string; bytes: number } | null;
  uploadedBy: { userId: number; name: string } | null;
  /** `agent` for the CLI and the agent API. */
  source: string;
  createdAt: string;
  storedAt: string | null;
  archivedAt: string | null;
  /** Web only: where the page plays, downloads and fetches the subtitles. */
  urls?: { video: string; download: string; srt: string | null };
}

/** One language of a variant on the admin page: its current version, and every version newest first. */
export interface TranslationLanguageEntry {
  code: string;
  current: TranslationFile | null;
  versions: TranslationFile[];
  /** Drive and the sheet; null when the delivery is off or nothing is current. */
  delivery: TranslationDelivery | null;
}

/**
 * Where a translation stands in the team's Drive folder and the languages
 * sheet (docs/translations/03-delivery.md): its current version uploaded to
 * Drive, and a row in the sheet linking it.
 */
export interface TranslationDelivery {
  /** `uploaded`: Drive holds the current version; `outdated`: an older one; `missing`: not uploaded. */
  drive: "uploaded" | "outdated" | "missing";
  /** `in_sheet`: a row links the Drive file; `missing`: none, never added or deleted from the sheet since. */
  sheet: "in_sheet" | "missing";
  /** The row's Facebook status (`R2L`, `Published`), as last read. */
  facebookStatus: string | null;
  /** The file in Drive. */
  driveUrl: string | null;
  /** Its name in Drive and the sheet: the original's, with the language first (`DA|CRIS|3014|...`). */
  name: string | null;
  /** The translated ad copy in the sheet's Ad Copy column. */
  adCopy: TranslationAdCopy;
  /** A sync on its way (`queued`, `running`) or stopped (`failed`, with the reason). */
  job: TranslationDeliveryJob | null;
  /** Drive has the current version, the sheet has its row, and the row has its ad copy. */
  done: boolean;
}

/**
 * The ad copy that goes beside a translated video (docs/translations/04-ad-copy.md):
 * the original's English copy on Meta, translated by Claude.
 */
export interface TranslationAdCopy {
  /** `pending`: not written yet; `done`: in the sheet; `failed`: see `error`; `no_source`: Meta has no copy for the original. */
  status: "pending" | "done" | "failed" | "no_source";
  error: string | null;
  /** The translated primary text and headline; on the hook's drawer only, null in the list. */
  text: string | null;
  headline: string | null;
}

export interface TranslationDeliveryJob {
  status: "queued" | "running" | "failed";
  /** While running: `drive` or `sheet`. */
  step: string | null;
  /** The last error, also while a queued one waits to retry. */
  error: string | null;
  attempts: number;
  nextAttemptAt: string | null;
}

/** The page's view of the delivery as a whole; null on the overview when it is not configured. */
export interface TranslationDeliverySummary {
  sheetUrl: string;
  folderUrl: string;
  /** Languages delivered by themselves when a translation finishes: the pinned ones. */
  autoLanguages: string[];
  /** Current translations in those languages that Drive or the sheet lacks, with no sync on its way. */
  pending: number;
  /** Of those, the ones whose last sync failed. */
  failed: number;
  /** Syncs queued or running now. */
  active: number;
}

/** POST /api/admin/translations/deliveries: one translation, or every one not yet delivered. */
export type TranslationDeliveryRequest = { variantKey: string; language: string } | { all: true };

export interface TranslationDeliveryQueued {
  queued: number;
}

/** A language a hook has: how many live versions, and when the newest arrived. */
export interface TranslationHookLanguage {
  code: string;
  versions: number;
  updatedAt: string;
  /** Drive and the sheet; null when the delivery is off. */
  delivery: TranslationDelivery | null;
}

/**
 * One hook of a concept on the admin page: a hook variant the platform knows
 * of (the tracker, Meta's library, the concept's hook rail, or a translation),
 * translated or not.
 */
export interface TranslationHook {
  variantKey: string;
  /** The URL form (`1-3014-2-x`), what the drawer opens. */
  slug: string;
  hook: number | null;
  iteration: number | null;
  driveFileId: string | null;
  /** The languages with a current version, pinned first. Empty: no translations. */
  languages: TranslationHookLanguage[];
  /** The newest upload among them; null when there is none. */
  updatedAt: string | null;
}

/**
 * One entry of the admin list: a concept, which is an editor and a concept
 * number together (a number is not unique across editors), with every hook of
 * it the platform knows. Only concepts with at least one translation are listed.
 */
export interface TranslationConcept {
  /** `editorId/conceptId` (`none` for a translation whose original had no concept number). */
  key: string;
  editorId: number;
  editorName: string | null;
  editorCode: string | null;
  editorColor: string | null;
  conceptId: number | null;
  /** By hook number, then iteration (the original first). */
  hooks: TranslationHook[];
  /** Summed over the hooks; null when Meta has run none of them. */
  /** The newest upload in the concept. */
  updatedAt: string;
}

/** GET /api/admin/translations. */
export interface TranslationsOverview {
  pinned: string[];
  summary: {
    /** Concepts with at least one translated hook. */
    concepts: number;
    /** Hooks with at least one current translation. */
    variants: number;
    /** Current translations (hook x language). */
    translations: number;
    /** Distinct languages with at least one. */
    languages: number;
    /** Per language, how many hooks have it. */
    perLanguage: Array<{ code: string; variants: number }>;
  };
  /** Newest translation first. */
  concepts: TranslationConcept[];
  /** Drive and the sheet; null when the delivery is off. */
  delivery: TranslationDeliverySummary | null;
}

/** Why a queued job is not running yet, after something went wrong. */
export interface TranslationJobRetry {
  /** `retry`: backing off after an error; `credits`: its provider is out of credits and the kind is paused. */
  waitingFor: "retry" | "credits";
  /** When it runs again (for `credits`, when the pause ends). */
  nextAttemptAt: string | null;
  /** Runs so far. */
  attempts: number;
  /** A rejected input or a long wait: retried slowly, hours apart. */
  longBackoff: boolean;
  error: TranslationJobErrorView;
}

/** A job's last error, in a code the page words (`describeJobError`) and as the worker said it. */
export interface TranslationJobErrorView {
  code: JobErrorCode;
  provider: "elevenlabs" | "audioshake" | null;
  detail: string;
  class: string;
  step: string | null;
}

/** A translate job on the cloud worker that has not finished: queued, or running at a step. */
export interface TranslationActivityJob {
  id: number;
  variantKey: string;
  slug: string;
  language: string;
  status: "queued" | "running";
  /** The worker's step while running: download, stems, style, transcribe-source, dub, mix, transcribe-dub, render, upload. */
  step: string | null;
  detail: string | null;
  /** 0-100 when the step reports it. */
  pct: number | null;
  createdAt: string;
  startedAt: string | null;
  /** Set while it waits to run again after an error. */
  retry: TranslationJobRetry | null;
}

/**
 * A job that needs a person to look at it: waiting to retry after errors, or
 * (from before every job was kept in the queue) stopped as failed. Jobs only
 * waiting for credits are not here: the quota banner speaks for them.
 */
export interface TranslationAttentionJob {
  id: number;
  batchId: number | null;
  variantKey: string;
  slug: string;
  editorId: number;
  editorName: string | null;
  conceptId: number | null;
  hook: number | null;
  iteration: number | null;
  language: string;
  /** `queued`: it will run again by itself at `nextAttemptAt`; `failed`: stopped, only Retry starts it. */
  status: "queued" | "failed";
  attempts: number;
  nextAttemptAt: string | null;
  longBackoff: boolean;
  error: TranslationJobErrorView;
  /** When it last failed. */
  failedAt: string;
}

/** A job held back by a provider's credits: one line of the banner. */
export interface TranslationQuotaWaitingJob {
  id: number;
  variantKey: string;
  slug: string;
  editorName: string | null;
  conceptId: number | null;
  hook: number | null;
  iteration: number | null;
  language: string;
  /** `running`: it is trying again right now. */
  status: "queued" | "running";
  /** The credits this video needs, when the provider said. */
  required: number | null;
  /** The video's length, when known. */
  durationMs: number | null;
  /** When it tries again by itself (null while it runs). */
  nextAttemptAt: string | null;
}

/**
 * A provider's credits holding translations back: the banner at the top of
 * the page. It exists only while a job waits on it (`waiting`), so it can
 * never outlive the work it is about.
 */
export interface TranslationQuotaBanner {
  provider: "elevenlabs" | "audioshake";
  /**
   * `out`: the account is out of credits and the kind is paused (one job at a
   * time finds out whether they are back); `short`: it has credits, but not
   * enough for the videos waiting (shorter ones carry on); `fallback`:
   * AudioShake is out and translations carry on without separated stems.
   */
  state: "out" | "short" | "fallback";
  /** True when translations wait for it (`out` and `short`). */
  paused: boolean;
  /** Refusals since it ran out. */
  failures: number;
  since: string;
  /** The kind Try now acts on ("translate"). */
  kind: string | null;
  /** When the next job tries again by itself; null while one is trying. */
  nextAttemptAt: string | null;
  /** A job is trying again now. */
  probing: boolean;
  /** Credits left and the most a waiting video needs, when the provider said. */
  credits: { remaining: number; required: number } | null;
  /** The jobs waiting on it, the longest wait first. */
  waiting: TranslationQuotaWaitingJob[];
  message: string;
  topUpUrl: string;
}

/** What one provider takes at once, as learned (`provider-limits.ts`). */
export interface TranslationProviderLimit {
  provider: string;
  level: number;
  probing: number | null;
  inUse: number;
  ceiling: number;
  cooldownUntil: string | null;
}

/** GET /api/admin/translations/activity: what the cloud worker is doing with translations. */
export interface TranslationActivity {
  running: number;
  queued: number;
  /** Newest first. */
  jobs: TranslationActivityJob[];
  /** Jobs waiting to retry after errors, and any stopped as failed; newest failure first. */
  attention: TranslationAttentionJob[];
  /** Providers out of credits. */
  quota: TranslationQuotaBanner[];
  /** Concurrency per provider, learned. */
  providers: TranslationProviderLimit[];
  workers: {
    /** Worker processes seen in the last two minutes. */
    online: number;
    /** The burst helper machine, when one is up. */
    helper: { id: string; since: string } | null;
  };
  /** For the submit modal's estimate: how long a job has taken lately, and how many run side by side. */
  throughput: { samples: number; medianSeconds: number | null; parallel: number };
  /** When the last translate job finished, done or failed. */
  lastFinishedAt: string | null;
}

/* ----------------------------------------------------------- submitting */

/** One candidate hook variant for a clean video. */
export interface TranslationVariantCandidate {
  variantKey: string;
  slug: string;
  editorId: number;
  editorName: string | null;
  conceptId: number | null;
  hook: number | null;
  iteration: number | null;
  /** The original's name in the tracker or Meta. */
  name: string | null;
}

/** POST /api/admin/translations/submit/match: what each file name is. */
export interface TranslationNameMatch {
  fileName: string;
  status: "matched" | "ambiguous" | "unmatched";
  /** Matched: the one variant. */
  variant: TranslationVariantCandidate | null;
  /** Ambiguous: every variant it could be. */
  candidates: TranslationVariantCandidate[];
  /** What the name parsed to, for the picker's starting point. */
  parsed: { conceptId: number | null; hook: number | null; editorId: number | null; iteration: number | null };
  /** Unmatched: why, in English (the page words the common cases itself). */
  reason: "no_concept" | "unknown_variant" | null;
}

/** A video found behind a Drive link. */
export interface TranslationDriveFile {
  driveFileId: string;
  resourceKey: string | null;
  name: string;
  bytes: number | null;
  mimeType: string;
  /** The folder it was found in, for a folder link. */
  folder: string | null;
}

/** POST /api/admin/translations/submit/drive. */
export interface TranslationDriveResult {
  files: TranslationDriveFile[];
  errors: Array<{ link: string; code: "not_a_link" | "not_shared" | "not_found" | "not_video" | "empty_folder" | "drive_unavailable"; message: string }>;
  /** Who to share a folder with when it is not "anyone with the link". */
  serviceAccount: string | null;
}

/** GET /api/admin/translations/variants/:slug. */
export interface TranslationVariantDetail {
  variant: TranslationVariant;
  currency: string;
  languages: TranslationLanguageEntry[];
  /** The original's English ad copy on Meta, once it has been read; null before. */
  adCopy: { primaryText: string; headline: string | null; description: string | null } | null;
}

/** GET and PUT /api/admin/translations/settings. */
export interface TranslationSettings {
  pinned: string[];
}

/** The pinned languages a hook does not have yet, in pinned order: what "Translate" ticks for it. */
export function missingPinned(have: readonly string[], pinned: readonly string[]): string[] {
  const got = new Set(have);
  return pinned.filter((code) => !got.has(code));
}

/* ------------------------------------------------------------- performance */

/**
 * The language of a translated ad, read from its name (docs/translations/05-performance.md).
 *
 * A translation is published under the original's name with the language as a
 * new first field: `CRIS|3014|GUM|HOOK1|…` in Danish is `DA|CRIS|3014|GUM|HOOK1|…`
 * (`translatedName`). The rest of the name parses to the original's hook
 * variant, which is how a translated ad finds its creative. Null for a name
 * that does not start that way:
 *
 * - the first field must be a language this catalogue lists, case aside, so an
 *   editor tag (`ESP|2120|…`, David Espitia's) is never read as a language;
 * - something must follow it, and the next field must not be a bare number - in
 *   `DA|2120|…` the first field is an editor's tag in front of its concept
 *   number, not a language.
 */
export function translatedAdLanguage(name: string | null | undefined): string | null {
  const text = (name ?? "").trim();
  const bar = text.indexOf("|");
  if (bar <= 0) return null;
  const field = text.slice(0, bar).trim().toUpperCase();
  const language = TRANSLATION_LANGUAGES.find((l) => l.code.toUpperCase() === field);
  if (!language) return null;
  const rest = text.slice(bar + 1).trim();
  const next = rest.split("|")[0]!.trim();
  if (!next || /^\d+$/.test(next)) return null;
  return language.code;
}

/**
 * Meta's numbers for a set of translated ads over a range: sums, so a ratio is
 * always one sum over another (ROAS is `revenue / spend`), never an average of
 * per-ad rates.
 */
export interface TranslationStats {
  spend: number;
  /** Purchase value (`omni_purchase`). */
  revenue: number;
  purchases: number;
  /** Ads published in Meta, whatever the range: one with no spend in it still counts. */
  ads: number;
}

/** One language over the range: every translated ad in it, summed. */
export interface TranslationLanguagePerformance extends TranslationStats {
  code: string;
  /** Hook variants published in this language. */
  creatives: number;
}

/** One hook variant: its translated ads (never the original's), per language and blended. */
export interface TranslationVariantPerformance {
  variantKey: string;
  slug: string;
  editorId: number;
  editorName: string | null;
  editorCode: string | null;
  editorColor: string | null;
  conceptId: number | null;
  hook: number | null;
  iteration: number | null;
  /** The original's name, as the translated ads carry it without the language. */
  name: string | null;
  driveFileId: string | null;
  /** Every translated language, blended. */
  total: TranslationStats;
  /** The languages it is published in, by spend. */
  languages: Array<TranslationStats & { code: string }>;
}

/** GET /api/admin/translations/performance?from=&to=. Both dates inclusive; none means all time. */
export interface TranslationPerformance {
  from: string | null;
  to: string | null;
  currency: string;
  /** Every pinned language (zeros when nothing is published), then any other with an ad. */
  languages: TranslationLanguagePerformance[];
  /** Every translated hook variant published in Meta, by spend. */
  variants: TranslationVariantPerformance[];
  /** Every translated ad, blended. */
  total: TranslationStats;
}

/**
 * GET /api/translated?<range>: the hook variants with translated ads, and each
 * language's numbers over the leaderboard's range, for the small tag on a
 * creative's row. The translated ads already count toward the variant's own
 * figures there; this says how much of them is which language.
 */
export interface TranslatedVariantsResponse {
  currency: string;
  variants: Array<{ slug: string; languages: Array<TranslationStats & { code: string }> }>;
}
